import {ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';

let lastGeneratedNumber = 0;

const generateInputId = (): string => {
    lastGeneratedNumber++;
    return 'input_' + lastGeneratedNumber;
};

@Component({
    selector: 'input[materialInput]',
    styleUrls: ['material-input.component.scss'],
    template: `<ng-content></ng-content>`,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialInputComponent implements OnInit, OnDestroy {

    private initialized: boolean = false;
    private inputId: string;
    private placeholderText: string;
    private additionalClass: string;
    private labelElement: HTMLElement;

    @Input()
    public set materialInput(additionalLabelClass: string) {
        const previousAdditionalClass = this.additionalClass;
        this.additionalClass = additionalLabelClass;

        if (this.initialized) {
            this.updateLabelClass(previousAdditionalClass);
        }
    }

    @Input()
    public set placeholder(placeholder: string) {
        this.placeholderText = placeholder;

        if (this.initialized) {
            this.updateLabel();
        }
    }

    @Input()
    public set id(id: string) {
        this.inputId = id;

        if (this.initialized) {
            this.updateRelation();
        }
    }

    constructor(
        private renderer: Renderer2,
        private inputElement: ElementRef
    ) {}

    public ngOnInit(): void {
        this.initialized = true;
        this.appendLabelElement();
        this.updateRelation();
        this.updateLabel();
        this.updateLabelClass('');
        this.onChange();
        this.subscribeToUpdates();
    }

    public ngOnDestroy(): void {
        const parentElement = this.renderer.parentNode(this.labelElement);
        this.renderer.removeChild(parentElement, this.labelElement);
    }

    private appendLabelElement(): void {
        this.labelElement = this.renderer.createElement('label');
        const labelElement = this.labelElement;
        const nextElement = this.renderer.nextSibling(this.inputElement.nativeElement);
        const parentElement = this.renderer.parentNode(this.inputElement.nativeElement);

        if (nextElement) {
            this.renderer.insertBefore(parentElement, labelElement, nextElement);
        } else {
            this.renderer.appendChild(parentElement, labelElement);
        }
    }

    private updateLabel(): void {
        this.labelElement.textContent = this.placeholderText;
        (this.inputElement.nativeElement as HTMLInputElement).placeholder = this.placeholderText;
    }

    private updateLabelClass(previousAdditionalClass: string): void {
        if (previousAdditionalClass) {
            this.renderer.removeClass(this.labelElement, previousAdditionalClass);
        }

        if (this.additionalClass) {
            this.renderer.addClass(this.labelElement, this.additionalClass);
        }
    }

    private updateRelation(): void {
        if (!this.inputId) {
            this.inputId = generateInputId();
        }

        this.renderer.setAttribute(this.inputElement.nativeElement, 'id', this.inputId);
        this.renderer.setAttribute(this.labelElement, 'for', this.inputId);
    }

    private onChange(): void {
        const inputElement = this.inputElement.nativeElement;
        const value = inputElement.value;

        if (value !== '') {
            this.renderer.setAttribute(inputElement, 'placeholder', '');
            this.renderer.removeStyle(this.labelElement, 'display');
        } else {
            this.renderer.setAttribute(inputElement, 'placeholder', this.placeholderText);
            this.renderer.setStyle(this.labelElement, 'display', 'none');
        }
    }

    private subscribeToUpdates(): void {
        const inputElement = this.inputElement.nativeElement;
        this.renderer.listen(inputElement, 'input', this.onChange.bind(this));
    }

}
